import { defineStore } from "pinia";
import type { CybersecurityScoreResponse } from "shared-core/types/cybersecurity-score";
import type { OrganizationOverview } from "shared-core/types/organization";
import type { MfaSettingsResponse } from "shared-core/types/settings";
import {
  MARK_COMPLETIONS_SEEN_MAX_BATCH,
  type MarkCompleteRequest,
  type MarkCompleteResponse,
  type MarkCompletionsSeenRequest,
  type MarkSkippedRequest,
  type MarkSkippedResponse,
  type SkipReason,
  type Task,
  type TaskAutoFixStatusResponse,
  TaskAutomationStatus,
  type TaskCompletionMethod,
  type TaskId,
  type TaskMilestoneEnum,
  type TasksResponse,
  type TaskStatusResponse,
} from "shared-core/types/task";
import { type EmployeesResponse } from "shared-core/types/user";
import { buildUserFullName } from "shared-core/utils/all-utils";
import { useDeviceService } from "../services/device.ts";
import { formatTaskLabels } from "../services/tasks.ts";
import type { TaskStatusSyncOptions } from "../types/device-task-automation-service.ts";
import type { MainStoreActions, MainStoreState } from "../types/main-store.ts";
import { useApi } from "../utils/api.ts";
import {
  taskDeviceInventoryId,
  taskVisibleDeviceInventoryId,
} from "../utils/task-utils.ts";

interface State extends MainStoreState {
  cyberScoreStatus: null | CybersecurityScoreResponse;
}

export function findTaskByIdAndDeviceInventoryId(
  tasks: Task[],
  taskId: TaskId,
  userEmail: string,
  deviceInventoryId?: string | null,
): Task | undefined {
  return tasks.find(
    (task) =>
      task.id === taskId &&
      task.user === userEmail &&
      taskDeviceInventoryId(task) === (deviceInventoryId ?? null),
  );
}

export const useMainStore = defineStore("main", {
  state: (): State => ({
    tasks: [],
    cyberScoreStatus: null,
    users: [],
    organizationOverview: null,
    mfaStatus: null,
    milestones: { achieved: [], upcoming: [] },
  }),
  actions: {
    async fetchTasks(options?: TaskStatusSyncOptions): Promise<void> {
      const api = useApi();
      const deviceService = useDeviceService();

      // Sync device-derived task status (e.g. firewall, BitLocker) before fetching
      await deviceService.syncTaskStatus(options);

      const response = await api.get<TasksResponse>("actions");
      const data = await response.data;
      let tasks = data.tasks;

      tasks = formatTaskLabels(tasks);

      tasks.forEach((task) => {
        if (
          task.automationStatus?.status ===
          TaskAutomationStatus.AUTOMATABLE_AGENT
        ) {
          const reasonKey = deviceService.automationUnavailableReasonKey(
            task.fixReference,
          );
          task.automationStatus.canAutomate =
            reasonKey === null &&
            deviceService.supportsAutomation(task.fixReference);
          if (reasonKey !== null) {
            task.automationStatus.reasonKey = reasonKey;
          }
        }
      });

      this.tasks = tasks;
      this.milestones = data.milestones ?? { achieved: [], upcoming: [] };
    },

    async fetchCyberScore(): Promise<void> {
      const api = useApi();
      const response = await api.get<CybersecurityScoreResponse>("get-score");
      this.cyberScoreStatus = await response.data;
    },

    async fetchMfaStatus(): Promise<void> {
      const api = useApi();
      const response = await api.get<MfaSettingsResponse>(
        "integrations/google-workspace/mfa-status",
      );
      this.mfaStatus = await response.data;
    },

    async fetchUsers(options?: {
      includeDeactivated?: boolean;
    }): Promise<void> {
      const api = useApi();
      const response = await api.get<EmployeesResponse>(
        "list-employees",
        options?.includeDeactivated
          ? { searchParams: { includeDeactivated: "true" } }
          : undefined,
      );
      this.users = (await response.data).employees.map((user) => ({
        ...user,
        fullName: buildUserFullName(user),
      }));
    },

    async markAsComplete(
      taskId: TaskId,
      userEmail: string,
      deviceInventoryId?: string | null,
      completionMethod?: TaskCompletionMethod,
    ): Promise<TaskMilestoneEnum[]> {
      const api = useApi();
      const resolvedCompletionMethod = completionMethod ?? "manual";
      // An admin's list holds every member's row for a task id; flag only the viewer's own.
      const task = this.tasks.find(
        (a: Task) =>
          a.id === taskId &&
          a.user === userEmail &&
          (deviceInventoryId
            ? taskVisibleDeviceInventoryId(a) === deviceInventoryId
            : true),
      );
      const completeResponse = await api.post<MarkCompleteResponse>(
        "mark-action-complete",
        {
          json: {
            taskId: taskId,
            ...(deviceInventoryId && { deviceInventoryId }),
            completionMethod: resolvedCompletionMethod,
          } satisfies MarkCompleteRequest,
        },
      );
      const { milestones } = await completeResponse.data;

      if (task) {
        task.isCompleted = true;
        task.isCompletedOrSkipped = true;
        task.completionSeenAt = new Date().toISOString();
        await useDeviceService().onTaskCompleted(task);
      }

      return milestones;
    },

    async markAsSkipped(
      taskId: TaskId,
      userEmail: string,
      skipReason: SkipReason,
      skipReasonText?: string | null,
      skipDelayHours?: number | null,
      deviceInventoryId?: string | null,
    ): Promise<TaskMilestoneEnum[]> {
      const api = useApi();
      const task = findTaskByIdAndDeviceInventoryId(
        this.tasks,
        taskId,
        userEmail,
        deviceInventoryId,
      );
      const completeResponse = await api.post<MarkSkippedResponse>(
        "mark-action-skipped",
        {
          json: {
            taskId: taskId,
            deviceInventoryId: deviceInventoryId ?? null,
            ...(skipReasonText && { skipReasonText }),
            ...(skipDelayHours && { skipDelayHours }),
            skipReason: skipReason,
          } satisfies MarkSkippedRequest,
        },
      );
      const { milestones } = await completeResponse.data;

      if (task) {
        task.skippedReason = skipReason;
        task.isSkipped = true;
        task.isCompletedOrSkipped = true;
      }

      return milestones;
    },

    async markCompletionsSeen(taskIds: number[]): Promise<void> {
      if (taskIds.length === 0) return;
      const seen = new Set(taskIds);
      const now = new Date().toISOString();
      this.tasks.forEach((task) => {
        if (seen.has(task.taskId)) task.completionSeenAt = now;
      });
      const api = useApi();
      try {
        // The endpoint rejects arrays longer than the batch limit, and a user
        // can accumulate more unseen completions than that through repeated
        // reopen/completion cycles, so send in chunks.
        for (
          let i = 0;
          i < taskIds.length;
          i += MARK_COMPLETIONS_SEEN_MAX_BATCH
        ) {
          const batch = taskIds.slice(i, i + MARK_COMPLETIONS_SEEN_MAX_BATCH);
          await api.post("mark-completions-seen", {
            json: { taskIds: batch } satisfies MarkCompletionsSeenRequest,
          });
        }
      } catch (error) {
        // Best-effort: the block reappears on the next load.
        console.error("Failed to persist seen completions", error);
      }
    },

    async fetchTaskAutoFixStatus(
      taskId: number,
    ): Promise<TaskAutoFixStatusResponse> {
      const api = useApi();
      const response = await api
        .get<TaskAutoFixStatusResponse>(`task/auto-fix/status?taskId=${taskId}`)
        .then((response) => response.data);

      return response;
    },

    async refreshTaskStatus(taskId: number): Promise<TaskStatusResponse> {
      const api = useApi();
      const response = await api.get<TaskStatusResponse>(
        `task/refresh-status?taskId=${taskId}`,
      );

      return response.data;
    },

    async automate(
      taskId: number,
      taskReference: string,
    ): Promise<TaskStatusResponse> {
      const api = useApi();
      const response = await api.post<TaskStatusResponse>(`task/automate`, {
        json: {
          taskId: taskId,
          taskReference: taskReference,
        },
      });

      return response.data;
    },

    async fetchOrganizationOverview(
      forceUpdate: boolean = false,
    ): Promise<void> {
      if (this.organizationOverview && !forceUpdate) {
        return;
      }

      const api = useApi();
      const response = await api.get<OrganizationOverview>(
        `organization/overview`,
      );

      this.organizationOverview = await response.data;
    },

    async startRiskAnalysis(): Promise<void> {
      const api = useApi();
      await api.post("risk-analysis");
    },
  } satisfies MainStoreActions & ThisType<State & MainStoreActions>,
  getters: {
    organizationsTasks(): Task[] {
      return this.tasks;
    },

    threatsIdentified(): number {
      return this.tasks.length;
    },
  },
});
